import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { useGameSlug, usePricingData } from '@pkg/hooks';

import { type EsCollection, type FloorpriceHistoryItem } from '@app/client';
import { callRecentCollections } from '@app/client/resources/competitions';
import { FANTASY_RECENT_COLLECTIONS_CACHE } from '@app/client/resources/fantasy';
import { type RecentCollection, useFantasyRecentPicks } from '@app/store/zustand';

//TODO find out if this will always return in this structure
export const formatRecentlyPickedData = (
    recentlyPickedCollection: EsCollection,
    floorPriceChange?: FloorpriceHistoryItem,
): RecentCollection => {
    const {
        current_owners_count,
        listed_assets_count,
        last_sale,
        id,
        spam_score,
        address: reservoir_id,
        slug,
        key_image,
        sale_counts,
        player_count,
        total_supply: tokenCount,
        ...metadata
    } = recentlyPickedCollection;

    return {
        salesVolume: sale_counts?.[0],
        player_count,
        metadata: { ...metadata, image_url: key_image },
        daas_id: id,
        floor_price_wei: metadata.floor_price,
        reservoir_id,
        spam_score,
        slug,
        tokenCount,
        floorPriceChange,
    } as unknown as RecentCollection;
};
export function useGetRecentlyPickedCollections(isUpdate = false) {
    const { addCollections, recentlyPickedCollections } = useFantasyRecentPicks();
    const getPricingData = usePricingData();
    const queryClient = useQueryClient();
    const gameSlug = useGameSlug();

    const getRecentlyPickedCollections = useCallback(async () => {
        const cache = [FANTASY_RECENT_COLLECTIONS_CACHE, gameSlug];
        const recentPicksResponse = await queryClient.fetchQuery(
            cache,
            ({ signal }) =>
                callRecentCollections({
                    slug: gameSlug,
                    settings: { signal },
                }),
            {
                retry: false,
            },
        );
        let collections: RecentCollection[] = [];
        if (recentPicksResponse?.ok) {
            if (recentPicksResponse.value && recentPicksResponse.value.collections?.length) {
                const slugs = recentPicksResponse.value.collections?.map((c) => c.slug || '');
                const pricingData = await getPricingData({ days: 7, slugs });
                collections = recentPicksResponse.value.collections?.map((recentPick) => {
                    const floorPriceChange =
                        pricingData?.floorPriceChanges?.find((res) => res.slug === recentPick.slug) || undefined;
                    return formatRecentlyPickedData(recentPick, floorPriceChange);
                });

                if (isUpdate) {
                    const recentPicks = collections?.reduce(
                        (acc, collection) => {
                            const existingItem = recentlyPickedCollections?.find(
                                (existing) => existing.slug === collection.slug,
                            );

                            if (existingItem && !isEqual(collection, existingItem)) {
                                const existingIndex = acc.updated.indexOf(existingItem);

                                acc.updated[existingIndex] = collection;
                            }
                            if (!existingItem) {
                                acc.new.push(collection);
                            }
                            return acc;
                        },
                        {
                            updated: recentlyPickedCollections as RecentCollection[],
                            new: [] as RecentCollection[],
                        },
                    );
                    addCollections('queuedCollections', recentPicks.new);
                    addCollections('recentlyPickedCollections', recentPicks.updated);
                } else {
                    addCollections('recentlyPickedCollections', collections);
                }
            }
        } else {
            return recentPicksResponse;
        }
    }, [addCollections, gameSlug, getPricingData, isUpdate, queryClient, recentlyPickedCollections]);
    return { getRecentlyPickedCollections };
}
